import { useEffect, useState } from 'react';
import { fechaNegocio, formatearCOP, formatearFecha, ventaPromedio } from '@tanteo/compartido';
import { avisosDelNegocio, resumenDia, type AvisoOperacion, type ResumenCuenta } from '../lib/api';
import { resumenDeAviso, useDetalleAviso } from '../lib/avisos';
import { useApp } from '../lib/contexto';
import { Esqueleto, EstadoVacio, MensajeError, Tarjeta } from '../ui/base';
import type { TotalesVentas } from '@tanteo/compartido';

/** El motivo de un fallo, para decirlo en la pantalla: un fallo nunca se pinta como un día en ceros. */
const motivo = (e: unknown) => (e instanceof Error ? e.message : 'Vuelve a intentar.');

export function Hoy() {
  const { negocio } = useApp();
  const [resumen, setResumen] = useState<{ cuentas: ResumenCuenta[]; totales: TotalesVentas } | null>(null);
  const [errorDia, setErrorDia] = useState('');
  /** Lo que las vigilancias encontraron en los últimos siete días. Un fallo al leerlos no tapa el día: se dice en su lugar. */
  const [avisos, setAvisos] = useState<AvisoOperacion[]>([]);
  const [errorAvisos, setErrorAvisos] = useState('');

  useEffect(() => {
    if (!negocio) return;
    resumenDia(negocio.negocioId, fechaNegocio())
      .then(setResumen)
      .catch((e) => setErrorDia(motivo(e)));
    avisosDelNegocio(negocio.negocioId)
      .then(setAvisos)
      .catch((e) => setErrorAvisos(motivo(e)));
  }, [negocio]);

  if (!negocio) return null;

  const pendientes = errorAvisos ? (
    <MensajeError e2e="avisos-error">No pudimos leer los pendientes de la semana. {errorAvisos}</MensajeError>
  ) : (
    avisos.length > 0 && (
      <Tarjeta data-e2e="avisos-operacion">
        <h2 className="mb-1 font-extrabold">Pendientes de la semana</h2>
        <p className="text-muted mb-3 text-contenido">
          Lo que el sistema encontró revisando los últimos siete días. Ninguno bloquea la venta.
        </p>
        <ul className="space-y-2">
          {avisos.map((a) => (
            <Aviso key={a.tipo} aviso={a} negocioId={negocio.negocioId} />
          ))}
        </ul>
      </Tarjeta>
    )
  );

  if (errorDia) {
    return (
      <div className="space-y-4">
        <h1 className="text-titulo font-extrabold">Hoy</h1>
        <MensajeError caja e2e="hoy-error">
          No pudimos leer las ventas de hoy. {errorDia}
        </MensajeError>
        {pendientes}
      </div>
    );
  }
  if (resumen === null) return <Esqueleto lineas={5} />;

  const { total, numVentas, numAnuladas, totalAnulado } = resumen.totales;
  const conVentas = resumen.cuentas.filter((r) => r.numPagos > 0 || r.numAnulados > 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-titulo font-extrabold">Hoy</h1>
        <p className="num text-total font-extrabold" data-e2e="hoy-total">
          {formatearCOP(total)}
        </p>
        {/* Franja de firma: decorativa, donde `docs/11-branding.md` §3 la manda. */}
        <div className="firma-degradado my-3 h-1.5 w-24 rounded-full" aria-hidden="true" />
        <p className="text-muted text-contenido">
          {numVentas} venta{numVentas === 1 ? '' : 's'} · venta promedio{' '}
          {formatearCOP(ventaPromedio(resumen.totales))}
        </p>
      </div>

      {conVentas.length === 0 && numAnuladas === 0 ? (
        <EstadoVacio titulo="Todavía no hay ventas hoy" detalle="Lo que cobres aparece aquí, cuenta por cuenta." />
      ) : (
        /* Las tarjetas se reparten el ancho disponible: con dos cuentas no dejan media pantalla vacía. */
        <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]" data-e2e="tarjetas-cuentas">
          {conVentas.map((r) => (
            <Tarjeta key={r.cuentaId ?? r.cuenta}>
              <p className="text-muted text-contenido font-bold uppercase">{r.cuenta}</p>
              <p className="num text-cifra font-extrabold">{formatearCOP(r.total)}</p>
              {/* Pagos y no ventas: una venta mixta deja un pago en cada cuenta, así que la suma
                  de las tarjetas no tiene por qué ser el número de ventas del día. */}
              <p className="text-muted text-etiqueta">
                {r.numPagos} pago{r.numPagos === 1 ? '' : 's'}
              </p>
            </Tarjeta>
          ))}
          {numAnuladas > 0 && (
            <Tarjeta>
              <p className="text-muted text-contenido font-bold uppercase">Anuladas</p>
              <p className="num text-cifra font-extrabold">{formatearCOP(totalAnulado)}</p>
              <p className="text-muted text-etiqueta">
                {numAnuladas} comprobante{numAnuladas === 1 ? '' : 's'}; no suman al total.
              </p>
            </Tarjeta>
          )}
        </div>
      )}

      {pendientes}

    </div>
  );
}

/** Un aviso con sus números y la lista de cuáles son, que se pide al desplegarlo. */
function Aviso({ aviso, negocioId }: { aviso: AvisoOperacion; negocioId: string }) {
  const { abierto, detalle, error, alternar } = useDetalleAviso(negocioId, aviso.tipo);
  const esFecha = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);

  return (
    <li className="border-border rounded-control border p-3" data-e2e="aviso">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-bold">{aviso.titulo}</span>
        <span className="text-muted text-contenido" data-e2e="aviso-numeros">
          {resumenDeAviso(aviso)}
        </span>
        <button
          onClick={alternar}
          aria-expanded={abierto}
          className="text-info text-contenido underline"
          data-e2e="aviso-desplegar"
        >
          {abierto ? 'ocultar' : 'ver cuáles'}
        </button>
      </div>
      {abierto && (
        <div className="mt-2" data-e2e="aviso-detalle">
          {error !== '' && <MensajeError>{error}</MensajeError>}
          {detalle === null && error === '' && <Esqueleto lineas={1} />}
          {detalle !== null && detalle.length > 0 && (
            <p className="text-muted text-contenido">
              {detalle.map((d) => (esFecha(d.dato) ? formatearFecha(d.dato) : d.dato)).join(' · ')}
            </p>
          )}
        </div>
      )}
      <p className="text-muted mt-1 text-contenido">{aviso.queHacer}</p>
    </li>
  );
}
